import * as React from "react"
import { useEffect, useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import {
	Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Link, LinearProgress, ListItemIcon,
	Menu, MenuItem, Paper,
	Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Tooltip, Typography,
} from "@mui/material"
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CalculateIcon from '@mui/icons-material/Calculate'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckIcon from '@mui/icons-material/Check'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import HubIcon from '@mui/icons-material/Hub'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import HistoryIcon from '@mui/icons-material/History'
import CloseIcon from '@mui/icons-material/Close'

import { BACKEND_APIS, VISIBILITY_TRACKER_ROUTES } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"
import SectionHistoryDrawer from "../SectionHistoryDrawer"

import { vtApi as api } from "../api"
import { todayISO } from "../metricsUtils"
import {
	retailers, items, counts, resourcesNeedsQa,
	selectedDate, selectedRetailerId, selectedStage, selectedCountry,
	selectedSectionKind, exportFailingOnly,
	isLoading, busyTaskIds, clearPipelineSignals,
} from "./@signals"


// Pipeline stages, in the order tasks traverse them. The dashboard renders
// counter chips in this order so the eye reads left-to-right as work
// flows through the pipeline. 'matched' sits between scored and ai_reviewed
// because the resource-dedup pass runs after scoring and before AI review
// inherits its matched_resource_id flags. In practice both fire in parallel
// from scoring, so a task may skip 'matched' visually if AI review wins the
// race — that's expected.
const STAGES = ['pending', 'in_progress', 'extracted', 'scored', 'matched', 'ai_reviewed', 'reviewed', 'failed']

const STAGE_COLOR = {
	pending:     'default',
	in_progress: 'warning',
	extracted:   'info',
	scored:      'primary',
	matched:     'primary',
	ai_reviewed: 'secondary',
	reviewed:    'success',
	failed:      'error',
	// Visual-only stage. The back never stores this — it's derived in
	// effectiveStage() below for past-date web tasks that stayed at
	// 'pending'. They can't be captured anymore (the live site has
	// moved on), so we paint them in muted grey so the operator
	// recognises them as un-actionable at a glance.
	expired:     'default',
}

// Display-only stage transform: a past-date web task still at
// `pending` is dead in the water — the retailer's live site has moved
// on and nobody can extract data for that date anymore. We surface
// the situation as 'expired' so it's distinguishable from "I haven't
// started this yet". Newsletter tasks are immune: the JPGs in GCS
// don't expire.
function effectiveStage(it) {
	const stage = it.pipeline_stage || 'pending'
	if (stage === 'pending'
		&& (it.section?.kind || 'web') !== 'newsletter'
		&& it.task_date && it.task_date < todayISO()) {
		return 'expired'
	}
	return stage
}

// Per-action UX level based on the task's current stage.
//
//   'primary'   → highlighted: this is the natural next step.
//   'secondary' → enabled but faded: out-of-order re-run, useful in
//                 specific cases (e.g. rematch after a Phase 2 resource
//                 merge, AI review after a prompt tweak). Tooltip
//                 prefixed with "Re-run".
//   'disabled'  → not clickable: the action makes no sense yet (task
//                 hasn't gone through earlier stages) or is locked
//                 out by 'failed'.
//
// rescore stays a primary action at every stage that has a session_path
// because its only side-effect is refreshing visibility numbers; the back
// no longer regresses pipeline_stage on a successful rescore (see
// scoring_svc.py:_score_once).
const ACTION_LEVEL = {
	rematch: {
		scored:      'primary',
		matched:     'secondary',
		ai_reviewed: 'secondary',
		reviewed:    'secondary',
	},
	aiReview: {
		scored:      'secondary',
		matched:     'primary',
		ai_reviewed: 'secondary',
		reviewed:    'secondary',
	},
}
function actionLevelFor(action, stage) {
	return ACTION_LEVEL[action]?.[stage] || 'disabled'
}


// Export chip state machine — derived from the task's exported_at,
// reviewed_at and per-client export_status JSON. The four states are:
//
//   not_exported  task never exported (or no client mappings)
//   exported      exported_at >= reviewed_at, no per-client failures
//   stale         was exported before but task was re-reviewed since
//   failing       at least one client carries a non-null last_error
//
// Returns null when the task isn't at a stage where exports apply
// (anything before 'reviewed') so the chip stays hidden.
function exportState(it) {
	const stage = it.pipeline_stage || ''
	if (stage !== 'reviewed') return null
	const reviewedAt = it.reviewed_at ? Date.parse(it.reviewed_at) : 0
	const exportedAt = it.exported_at ? Date.parse(it.exported_at) : 0
	const status = it.export_status || {}
	const clients = Object.keys(status)
	const failing = clients.filter(c => status[c]?.last_error)
	if (failing.length > 0) return { state: 'failing', failing, status, exportedAt }
	if (!exportedAt) return { state: 'not_exported', status, exportedAt: 0 }
	if (reviewedAt && exportedAt < reviewedAt) return { state: 'stale', status, exportedAt }
	return { state: 'exported', status, exportedAt }
}

// Same rule that paints the row's export chip red, so the header counter
// and the "export fail" filter always agree with what the rows show.
const isExportFailing = (it) => exportState(it)?.state === 'failing'

// GCS errors carry Google's whole HTML error page after the first line
// ("502 POST https://storage.googleapis.com/…: <!DOCTYPE html>…"); the
// tooltip only needs the status and the URL.
const shortError = (err) => {
	const head = String(err).split(/\n|: <!DOCTYPE/i)[0]
	return head.length > 200 ? head.slice(0, 200) + '…' : head
}

const EXPORT_CHIP_STYLES = {
	not_exported: { label: 'no export',  bg: '#bdbdbd', fg: '#212121' },  // grey
	exported:     { label: 'exported',   bg: '#2e7d32', fg: '#fff' },     // green 800
	stale:        { label: 'stale',      bg: '#f9a825', fg: '#212121' },  // amber 800
	failing:      { label: 'fail',       bg: '#c62828', fg: '#fff' },     // red 800
}

function ExportChip({ it }) {
	const info = exportState(it)
	if (!info) return null
	const style = EXPORT_CHIP_STYLES[info.state]
	const clients = Object.entries(info.status || {})
	const tip = (
		<Box>
			<Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
				Export to downstream clients
			</Typography>
			{clients.length === 0 ? (
				<Typography variant="caption" sx={{ display: 'block' }}>
					No client mapping populated for this section.
				</Typography>
			) : clients.map(([slug, s]) => (
				<Typography key={slug} variant="caption" sx={{ display: 'block' }}>
					{slug}: {s?.last_error
						? `❌ ${shortError(s.last_error)}`
						: s?.exported_at ? `✓ ${s.exported_at}` : '—'}
				</Typography>
			))}
		</Box>
	)
	return (
		<Tooltip title={tip}>
			<Chip size="small"
				label={style.label}
				sx={{ bgcolor: style.bg, color: style.fg, fontWeight: 700 }}
			/>
		</Tooltip>
	)
}


export default function PipelinePage() {
	const { showToast } = useAppToast()

	const loadRetailers = async () => {
		const r = await api.get(`${BACKEND_APIS.VT}/admin/retailers`)
		if (!r.ok) return showToast.error('Failed to load retailers: ' + r.error)
		retailers.value = r.data?.items || []
	}

	const loadPipeline = async () => {
		isLoading.value = true
		const params = new URLSearchParams({ date: selectedDate.value })
		if (selectedRetailerId.value) params.set('retailer', String(selectedRetailerId.value))
		if (selectedStage.value)      params.set('stage', selectedStage.value)
		if (selectedCountry.value)    params.set('country', selectedCountry.value)
		const r = await api.get(`${BACKEND_APIS.VT}/admin/pipeline?${params.toString()}`)
		isLoading.value = false
		if (!r.ok) return showToast.error('Failed to load pipeline: ' + r.error)
		items.value = r.data?.items || []
		counts.value = r.data?.counts || {}
		resourcesNeedsQa.value = r.data?.resources_needs_qa || 0
	}

	useEffect(() => {
		loadRetailers()
		loadPipeline()
		return clearPipelineSignals
	}, [])
	useEffect(() => { loadPipeline() }, [
		selectedDate.value, selectedRetailerId.value, selectedStage.value, selectedCountry.value,
	])

	const setBusy = (id, busy) => {
		const next = new Set(busyTaskIds.value)
		if (busy) next.add(id); else next.delete(id)
		busyTaskIds.value = next
	}

	const rescore = async (id_task) => {
		setBusy(id_task, true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/${id_task}/rescore`)
		setBusy(id_task, false)
		if (!r.ok) return showToast.error('Rescore failed: ' + r.error)
		showToast.success('Rescored')
		loadPipeline()
	}

	const retry = async (id_task) => {
		setBusy(id_task, true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/${id_task}/retry`)
		setBusy(id_task, false)
		if (!r.ok) return showToast.error('Retry failed: ' + r.error)
		showToast.success('Reset to ' + r.data?.pipeline_stage)
		loadPipeline()
	}

	const setStage = async (id_task, stage) => {
		setBusy(id_task, true)
		const r = await api.post(
			`${BACKEND_APIS.VT}/admin/tasks/${id_task}/set-stage`,
			{ stage },
		)
		setBusy(id_task, false)
		if (!r.ok) return showToast.error('Set-stage failed: ' + r.error)
		showToast.success(`Stage → ${stage}`)
		loadPipeline()
	}

	const aiReviewTask = async (id_task) => {
		setBusy(id_task, true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/${id_task}/ai-review`)
		setBusy(id_task, false)
		if (!r.ok) return showToast.error('Enqueue failed: ' + r.error)
		// Cloud Tasks runs the worker async — refresh the list a moment
		// later so the new pipeline_stage shows up without manual reload.
		showToast.success('AI review queued')
		setTimeout(loadPipeline, 1500)
	}

	const rematchTask = async (id_task) => {
		setBusy(id_task, true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/${id_task}/rematch`)
		setBusy(id_task, false)
		if (!r.ok) return showToast.error('Rematch enqueue failed: ' + r.error)
		showToast.success('Resource match queued')
		setTimeout(loadPipeline, 1500)
	}

	const reExportTask = async (id_task) => {
		setBusy(id_task, true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/${id_task}/export`)
		setBusy(id_task, false)
		if (!r.ok) return showToast.error('Re-export failed: ' + r.error)
		showToast.success('Export queued')
		setTimeout(loadPipeline, 1500)
	}

	// Bulk re-export every `reviewed` task currently in scope (date is
	// always required; retailer + section are inferred from the active
	// filters and the visible list). Confirms first because it can
	// enqueue many Cloud Tasks at once.
	const reExportFiltered = async () => {
		const date = selectedDate.value
		if (!date) return showToast.warning('Pick a date first.')
		const id_retailer = selectedRetailerId.value || ''
		const section_kind = selectedSectionKind.value || ''
		// Visible items already had the section_kind filter applied by
		// the TasksTable render layer; re-derive here so the count /
		// section-inference / confirmation message line up with what
		// the operator sees.
		const visible = section_kind
			? items.value.filter(it => it.section?.kind === section_kind)
			: items.value
		// With the "export fail" filter on, the bulk endpoint's scope
		// (date / retailer / section) would re-export far more than what
		// is on screen, so re-export exactly the red tasks, one by one.
		if (exportFailingOnly.value) {
			const ids = visible.filter(isExportFailing).map(it => it.id_task)
			if (!ids.length) return showToast.warning('No failed exports in scope.')
			if (!window.confirm(`Re-export ${ids.length} task(s) with a failed export (${date})?`)) return
			const results = await Promise.all(
				ids.map(id => api.post(`${BACKEND_APIS.VT}/admin/tasks/${id}/export`)))
			const failed = results.filter(r => !r.ok).length
			showToast.success(`Queued ${ids.length - failed} task(s)` + (failed ? ` (${failed} failed)` : ''))
			setTimeout(loadPipeline, 1500)
			return
		}
		// Section is inferred only when the visible list is already
		// narrowed to one — same rule as the AI-review-section button.
		const sectionIds = new Set(visible.map(i => i.section.id_section))
		const id_section = sectionIds.size === 1 ? visible[0].section.id_section : ''
		const scopeBits = [
			id_section ? 'section' : id_retailer ? 'retailer' : 'date',
			section_kind ? section_kind : null,
		].filter(Boolean)
		const reviewedCount = visible.filter(it => it.pipeline_stage === 'reviewed').length
		const confirmed = window.confirm(
			`Re-export ${reviewedCount} reviewed task(s) in scope (${scopeBits.join(' / ')}, ${date})? ` +
			`Each one queues a Cloud Task and overwrites the CSV in GCS.`
		)
		if (!confirmed) return
		const qs = new URLSearchParams()
		if (id_retailer)  qs.set('id_retailer',  id_retailer)
		if (id_section)   qs.set('id_section',   id_section)
		if (section_kind) qs.set('section_kind', section_kind)
		const url = `${BACKEND_APIS.VT}/admin/dates/${date}/export` + (qs.toString() ? `?${qs}` : '')
		const r = await api.post(url)
		if (!r.ok) return showToast.error('Re-export failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Queued ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
		setTimeout(loadPipeline, 1500)
	}

	// Bulk re-enqueue of the resource matcher over every task in scope.
	// Useful as a recovery action when a matcher bug parked tasks in
	// `scored` or left leaves without `matched_resource_id`. Hits
	// /admin/tasks/re-match-bulk — idempotent: tasks already healthy
	// resolve via HIT-only on the next pass.
	const reMatchFiltered = async () => {
		const date = selectedDate.value
		const id_retailer = selectedRetailerId.value || ''
		const section_kind = selectedSectionKind.value || ''
		const visible = section_kind
			? items.value.filter(it => it.section?.kind === section_kind)
			: items.value
		const sectionIds = new Set(visible.map(i => i.section.id_section))
		const id_section = sectionIds.size === 1 ? visible[0].section.id_section : ''
		const scopeBits = [
			id_section ? 'section' : id_retailer ? 'retailer' : 'date',
			section_kind ? section_kind : null,
		].filter(Boolean)
		const confirmed = window.confirm(
			`Re-match ${visible.length} task(s) in scope (${scopeBits.join(' / ')}, ${date})? ` +
			`Each one queues a Cloud Task. Idempotent — safe to repeat.`
		)
		if (!confirmed) return
		// /re-match-bulk requires at least one filter. We always have at
		// least `date_from = date` (Pipeline always has a date selected).
		const qs = new URLSearchParams()
		if (date)         { qs.set('date_from', date); qs.set('date_to', date) }
		if (id_retailer)  qs.set('id_retailer',  id_retailer)
		if (id_section)   qs.set('id_section',   id_section)
		if (section_kind) qs.set('section_kind', section_kind)
		const url = `${BACKEND_APIS.VT}/admin/tasks/re-match-bulk?${qs.toString()}`
		const r = await api.post(url)
		if (!r.ok) return showToast.error('Re-match failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Queued ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
		setTimeout(loadPipeline, 1500)
	}

	// Bulk rescore. Hits /admin/tasks/rescore-bulk which runs
	// ScoringService.run on each task — same fields written, idempotent
	// when the formula hasn't changed. Used after tweaking the
	// visibility formula to backfill scores on existing tasks.
	const reScoreFiltered = async () => {
		const date = selectedDate.value
		const id_retailer = selectedRetailerId.value || ''
		const section_kind = selectedSectionKind.value || ''
		const visible = section_kind
			? items.value.filter(it => it.section?.kind === section_kind)
			: items.value
		const sectionIds = new Set(visible.map(i => i.section.id_section))
		const id_section = sectionIds.size === 1 ? visible[0].section.id_section : ''
		const scopeBits = [
			id_section ? 'section' : id_retailer ? 'retailer' : 'date',
			section_kind ? section_kind : null,
		].filter(Boolean)
		const confirmed = window.confirm(
			`Re-score ${visible.length} task(s) in scope (${scopeBits.join(' / ')}, ${date})? ` +
			`Runs sequentially (~100ms each). Re-export afterwards to push the new scores into the CSVs.`
		)
		if (!confirmed) return
		const qs = new URLSearchParams()
		if (date)         { qs.set('date_from', date); qs.set('date_to', date) }
		if (id_retailer)  qs.set('id_retailer',  id_retailer)
		if (id_section)   qs.set('id_section',   id_section)
		if (section_kind) qs.set('section_kind', section_kind)
		const url = `${BACKEND_APIS.VT}/admin/tasks/rescore-bulk?${qs.toString()}`
		const r = await api.post(url)
		if (!r.ok) return showToast.error('Re-score failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Re-scored ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
		setTimeout(loadPipeline, 1500)
	}

	const aiReviewSection = async () => {
		// Bulk action — only sensible when one section is filtered (else
		// the call would target ALL sections of all retailers, which is
		// expensive). For now we infer the section from the items list:
		// if every visible task shares the same id_section, fire it.
		const sectionIds = new Set(items.value.map(i => i.section.id_section))
		if (sectionIds.size !== 1) {
			showToast.warning('Filter to a single section to bulk-AI-review.')
			return
		}
		const id_section = items.value[0].section.id_section
		const r = await api.post(
			`${BACKEND_APIS.VT}/admin/sections/${id_section}/ai-review?date=${selectedDate.value}`,
		)
		if (!r.ok) return showToast.error('Enqueue section failed: ' + r.error)
		const queued = r.data?.enqueued || 0
		const failed = r.data?.failed || 0
		showToast.success(
			`Queued ${queued} task${queued === 1 ? '' : 's'} ` +
			(failed ? `(${failed} failed to enqueue) ` : '') +
			`— refresh in a moment to see progress.`,
		)
		setTimeout(loadPipeline, 1500)
	}

	return <PageWrapper
		{...vtPageProps('Pipeline', 'Every task across the pipeline stages — extraction, scoring, review')}
	>
		<Filters aiReviewSection={aiReviewSection} reExportFiltered={reExportFiltered} reMatchFiltered={reMatchFiltered} reScoreFiltered={reScoreFiltered} onRefresh={loadPipeline} />
		<CountersStrip />
		<Box sx={{ mt: 2 }}>
			{isLoading.value ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
					<CircularProgress size={22} />
					<Typography variant="body2">Loading pipeline…</Typography>
				</Box>
			) : items.value.length === 0 ? (
				<Alert severity="info">No tasks for this filter combination.</Alert>
			) : (
				<TasksTable rescore={rescore} retry={retry} setStage={setStage}
					aiReviewTask={aiReviewTask} rematchTask={rematchTask}
					reExportTask={reExportTask} />
			)}
		</Box>
	</PageWrapper>
}


function Filters({ aiReviewSection, reExportFiltered, reMatchFiltered, reScoreFiltered, onRefresh }) {
	// Single-section context unlocks the bulk AI button. We rely on the
	// table's actual rows (after retailer / stage filters) to decide,
	// instead of reading the filter dropdowns — that way the button works
	// even when the user landed via deep-link without picking a retailer.
	const sectionIds = new Set(items.value.map(i => i.section.id_section))
	const canBulkAi = sectionIds.size === 1
	return <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
		<TextField size="small" label="Date" type="date" sx={{ width: 180 }}
			value={selectedDate.value}
			onChange={e => { selectedDate.value = e.target.value }}
			InputLabelProps={{ shrink: true }}
		/>
		<TextField select size="small" label="Country" sx={{ width: 130 }}
			value={selectedCountry.value}
			onChange={e => { selectedCountry.value = e.target.value }}
		>
			<MenuItem value="">All</MenuItem>
			{Array.from(new Set(retailers.value.map(r => (r.country || '').toUpperCase()).filter(Boolean))).sort().map(cc => (
				<MenuItem key={cc} value={cc}>{cc}</MenuItem>
			))}
		</TextField>
		<TextField select size="small" label="Retailer" sx={{ minWidth: 200 }}
			value={selectedRetailerId.value || ''}
			onChange={e => { selectedRetailerId.value = e.target.value ? Number(e.target.value) : null }}
		>
			<MenuItem value="">All</MenuItem>
			{retailers.value.filter(r => r.active).map(r => (
				<MenuItem key={r.id_retailer} value={r.id_retailer}>{r.slug}</MenuItem>
			))}
		</TextField>
		<TextField select size="small" label="Stage" sx={{ minWidth: 160 }}
			value={selectedStage.value || ''}
			onChange={e => { selectedStage.value = e.target.value || null }}
		>
			<MenuItem value="">All</MenuItem>
			{STAGES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
		</TextField>
		<TextField select size="small" label="Section kind" sx={{ minWidth: 140 }}
			value={selectedSectionKind.value || ''}
			onChange={e => { selectedSectionKind.value = e.target.value || '' }}
		>
			<MenuItem value="">All</MenuItem>
			<MenuItem value="web">web</MenuItem>
			<MenuItem value="newsletter">newsletter</MenuItem>
		</TextField>
		<Tooltip title="Refresh pipeline status (re-fetches without reloading the page)">
			<span>
				<Button size="small" variant="outlined"
					disabled={isLoading.value}
					startIcon={<RefreshIcon fontSize="small"
						sx={{ animation: isLoading.value ? 'vt-spin 0.8s linear infinite' : 'none',
							'@keyframes vt-spin': { to: { transform: 'rotate(360deg)' } } }}
					/>}
					onClick={onRefresh}
				>Refresh</Button>
			</span>
		</Tooltip>
		<Box sx={{ flexGrow: 1 }} />
		<Tooltip title={canBulkAi
			? "Run AI review on every task currently shown for this section"
			: "Filter the list down to a single section first"}
		>
			<span>
				<Button size="small" variant="outlined" color="secondary"
					startIcon={<AutoAwesomeIcon fontSize="small" />}
					disabled={!canBulkAi}
					onClick={aiReviewSection}
				>AI review section</Button>
			</span>
		</Tooltip>
		<Tooltip title="Re-export every reviewed task in scope (date + active retailer/section filter). Overwrites the CSV in GCS.">
			<span>
				<Button size="small" variant="outlined" color="warning"
					onClick={reExportFiltered}
				>Re-export filtered</Button>
			</span>
		</Tooltip>
		<Tooltip title="Re-enqueue the resource matcher for every task in scope. Recovery action — idempotent, safe to repeat.">
			<span>
				<Button size="small" variant="outlined" color="warning"
					onClick={reMatchFiltered}
				>Re-match filtered</Button>
			</span>
		</Tooltip>
		<Tooltip title="Recalculate visibility_score for every task in scope. Auto re-exports the reviewed ones so the new numbers reach the consumer.">
			<span>
				<Button size="small" variant="outlined" color="warning"
					onClick={reScoreFiltered}
				>Re-score filtered</Button>
			</span>
		</Tooltip>
	</Stack>
}


function CountersStrip() {
	const total = STAGES.reduce((acc, s) => acc + (counts.value[s] || 0), 0)
	// Sum of the per-retailer QA chips: task-grain, so it FOLLOWS the date
	// (and section-kind) filter — "how much entered human QA in this range".
	const kindFilter = selectedSectionKind.value
	const inKind = kindFilter
		? items.value.filter(it => it.section?.kind === kindFilter)
		: items.value
	const qaInRange = inKind.reduce((a, it) => a + (it.needs_qa_count || 0), 0)
	// Export-grain, not stage-grain: a task can be `reviewed` (green stage)
	// and still have a red export chip. Counted client-side over the loaded
	// tasks, so it follows the date / retailer / section-kind filters.
	const exportFailCount = inKind.filter(isExportFailing).length
	return <Paper variant="outlined" sx={{ mt: 2, p: 1.5 }}>
		<Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
			<Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>Total: {total}</Typography>
			{STAGES.map(s => (
				<Chip
					key={s}
					size="small"
					label={`${s}: ${counts.value[s] || 0}`}
					color={STAGE_COLOR[s]}
					variant={selectedStage.value === s ? 'filled' : 'outlined'}
					onClick={() => { selectedStage.value = selectedStage.value === s ? null : s }}
				/>
			))}
			<Tooltip title="Tasks whose export to a downstream client failed (upload or ACK to the consumer). Click to show only those.">
				<Chip
					size="small"
					label={`export fail: ${exportFailCount}`}
					color="error"
					variant={exportFailingOnly.value ? 'filled' : 'outlined'}
					onClick={() => { exportFailingOnly.value = !exportFailingOnly.value }}
				/>
			</Tooltip>
			<Box sx={{ flex: 1 }} />
			{qaInRange > 0 && (
				<Tooltip title="Sum of the per-retailer QA chips — items needing human QA within the current filters">
					<Chip
						size="small"
						label={`QA in range: ${qaInRange}`}
						color="error"
						variant="outlined"
						sx={{ fontWeight: 700 }}
					/>
				</Tooltip>
			)}
			{/* Resource-grain pending counter. Date-agnostic (resources
			    are retailer-wide), so the chip stays put when the operator
			    changes the date filter. Clicking it jumps to the
			    resource-centric Review Queue. */}
			<Tooltip title="Resource review queue — global, not affected by the date filter">
				<Chip
					size="small"
					clickable
					component="a"
					href="/visibility-tracker/review-queue"
					label={`${resourcesNeedsQa.value} resources pending QA`}
					color={resourcesNeedsQa.value > 0 ? 'warning' : 'success'}
					variant="filled"
					sx={{ fontWeight: 700 }}
				/>
			</Tooltip>
		</Stack>
	</Paper>
}


// Group the flat task list into one bucket per retailer, each with a
// completion summary, sorted: retailers with pending work first, then
// alphabetical by slug.
function groupByRetailer(rows) {
	const byId = new Map()
	for (const it of rows) {
		const id = it.retailer.id_retailer
		if (!byId.has(id)) byId.set(id, { retailer: it.retailer, rows: [] })
		byId.get(id).rows.push(it)
	}
	const groups = []
	for (const g of byId.values()) {
		const total = g.rows.length
		const done = g.rows.filter(it => (it.pipeline_stage || '') === 'reviewed').length
		const failures = g.rows.filter(it => it.failed_stage || effectiveStage(it) === 'failed').length
		const needsQa = g.rows.reduce((a, it) => a + (it.needs_qa_count || 0), 0)
		const byStage = {}
		for (const it of g.rows) {
			const s = effectiveStage(it)
			byStage[s] = (byStage[s] || 0) + 1
		}
		groups.push({
			...g, total, done, failures, needsQa, byStage,
			pct: total ? Math.round((done / total) * 100) : 0,
			hasPending: done < total || failures > 0,
		})
	}
	groups.sort((a, b) => {
		if (a.hasPending !== b.hasPending) return a.hasPending ? -1 : 1
		return (a.retailer.slug || '').localeCompare(b.retailer.slug || '')
	})
	return groups
}


function TasksTable({ rescore, retry, setStage, aiReviewTask, rematchTask, reExportTask }) {
	// Apply the client-side `section_kind` filter before grouping —
	// the backend list endpoint doesn't accept it (yet), and the filter
	// is purely a viewing/scoping concern. Bulk re-export passes the
	// same value as a server-side filter to /admin/dates/.../export.
	const kindFilter = selectedSectionKind.value
	const inKind = kindFilter
		? items.value.filter(it => it.section?.kind === kindFilter)
		: items.value
	const filtered = exportFailingOnly.value ? inKind.filter(isExportFailing) : inKind
	const groups = React.useMemo(() => groupByRetailer(filtered), [filtered])
	// Expanded retailer ids. Collapsed by default — with many retailers the
	// section detail is overwhelming; the operator opens the ones they care
	// about.
	const [expanded, setExpanded] = useState(() => new Set())
	const [historyId, setHistoryId] = useState(null)   // id_section whose drawer is open
	const toggle = (id) => setExpanded(prev => {
		const next = new Set(prev)
		next.has(id) ? next.delete(id) : next.add(id)
		return next
	})

	return <>
		<TableContainer component={Paper} variant="outlined">
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Retailer · Section</TableCell>
						<TableCell>Asset</TableCell>
						<TableCell>Stage</TableCell>
						<TableCell align="right">Attempts</TableCell>
						<TableCell>Timeline</TableCell>
						<TableCell>Tagger</TableCell>
						<TableCell align="right">Actions</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{groups.map(g => (
						<React.Fragment key={g.retailer.id_retailer}>
							<RetailerHeaderRow group={g} open={expanded.has(g.retailer.id_retailer)}
								onToggle={() => toggle(g.retailer.id_retailer)} />
							{expanded.has(g.retailer.id_retailer) && g.rows.map(it => (
								<TaskRow key={it.id_task} it={it}
									rescore={rescore} retry={retry} setStage={setStage}
									aiReviewTask={aiReviewTask} rematchTask={rematchTask}
									reExportTask={reExportTask}
									onHistory={() => setHistoryId(it.section.id_section)}
								/>
							))}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		</TableContainer>
		<SectionHistoryDrawer idSection={historyId} onClose={() => setHistoryId(null)} />
	</>
}


// Collapsible per-retailer summary row: completion progress + what's
// left and where. Spans the whole table width.
function RetailerHeaderRow({ group, open, onToggle }) {
	const { retailer, total, done, failures, needsQa, byStage, pct } = group
	const isDone = pct === 100 && failures === 0
	// Remaining buckets (everything not 'reviewed'), shown only when > 0.
	const remaining = STAGES
		.filter(s => s !== 'reviewed' && (byStage[s] || 0) > 0)
		.map(s => ({ s, n: byStage[s] }))
	const expiredN = byStage['expired'] || 0
	return <TableRow
		hover
		onClick={onToggle}
		sx={{ cursor: 'pointer', bgcolor: (t) => t.palette.action.hover, '& td': { borderBottom: '2px solid', borderBottomColor: 'divider' } }}
	>
		<TableCell colSpan={7} sx={{ py: 1 }}>
			<Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
				{open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
				{/* Fixed-width name+country block so the progress column
				    that follows always starts at the same x → bars line up
				    in a clean vertical column across all retailer rows. */}
				<Box sx={{ width: 240, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
					<Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{retailer.name}</Typography>
					{retailer.country && <Chip size="small" label={String(retailer.country).toUpperCase()} variant="outlined" />}
				</Box>
				{/* Progress column — ALWAYS rendered (full green bar when
				    done) and fixed-width, so every row is homogeneous. */}
				<Box sx={{ width: 240, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
					<LinearProgress variant="determinate" value={pct}
						color={failures > 0 ? 'error' : (isDone ? 'success' : 'primary')}
						sx={{ flex: 1, height: 8, borderRadius: 4 }} />
					<Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', minWidth: 78, textAlign: 'right' }}>
						{isDone ? 'DONE ✓' : `${done}/${total} · ${pct}%`}
					</Typography>
				</Box>
				<Box sx={{ flex: 1 }} />
				{/* What's left, and where. */}
				<Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5, justifyContent: 'flex-end' }}>
					{remaining.map(({ s, n }) => (
						<Chip key={s} size="small" variant="outlined"
							color={s === 'expired' ? 'default' : (STAGE_COLOR[s] || 'default')}
							label={`${s}: ${n}`} />
					))}
					{needsQa > 0 && (
						<Tooltip title={`${needsQa} item(s) pending human QA across this retailer`}>
							<Chip size="small" color="error" variant="filled" label={`QA: ${needsQa}`} sx={{ fontWeight: 700 }} />
						</Tooltip>
					)}
					{failures > 0 && (
						<Chip size="small" color="error" variant="outlined" label={`${failures} failed`} sx={{ fontWeight: 700 }} />
					)}
				</Stack>
			</Stack>
		</TableCell>
	</TableRow>
}


function TaskRow({ it, rescore, retry, setStage, aiReviewTask, rematchTask, reExportTask, onHistory }) {
	const busy = busyTaskIds.value.has(it.id_task)
	// Deep-link directly into Banners Review with retailer/section/date/asset
	// pre-selected. The Review page consumes these query params on mount and
	// walks its own cascading load chain (retailers → sections → session).
	const reviewParams = new URLSearchParams({
		id_retailer: String(it.retailer.id_retailer),
		id_section: String(it.section.id_section),
		date: it.task_date,
	})
	if (it.asset_path) reviewParams.set('asset_path', it.asset_path)
	const reviewHref = `${VISIBILITY_TRACKER_ROUTES.REVIEW_PATH}?${reviewParams.toString()}`
	const stage = effectiveStage(it)
	const isFailed = stage === 'failed'
	// A task can carry a failed_stage without pipeline_stage being
	// 'failed' — e.g. when the AI-review chain failed AFTER the matcher
	// successfully bumped the stage. Surface that as a red chip too,
	// otherwise the failure is invisible until someone reads SQL.
	const hasFailureMarker = Boolean(it.failed_stage)

	return <TableRow hover>
		<TableCell>
			<Typography variant="body2">{it.retailer.slug} · {it.section.slug}</Typography>
			{it.section.kind === 'newsletter' && (
				<Typography variant="caption" sx={{ color: 'info.main' }}>newsletter</Typography>
			)}
		</TableCell>
		<TableCell sx={{ maxWidth: 260 }}>
			{it.asset_path ? (
				it.asset_url ? (
					<Link
						href={it.asset_url}
						target="_blank"
						rel="noopener noreferrer"
						underline="hover"
						variant="caption"
						sx={{ wordBreak: 'break-all' }}
					>
						{it.asset_path}
					</Link>
				) : (
					<Typography variant="caption" sx={{ wordBreak: 'break-all', color: 'text.secondary' }}>
						{it.asset_path}
					</Typography>
				)
			) : (
				<Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
			)}
		</TableCell>
		<TableCell>
			<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
				{stage === 'expired' ? (
					// Dark muted brown + uppercase + bold so it reads as
					// "dead, untouchable" at a glance. Deliberately NOT
					// red — `failed` is already red and an expired task
					// is a fundamentally different state (it ran out of
					// time, nothing broke).
					<Chip
						size="small"
						label="EXPIRED"
						sx={{
							bgcolor: '#5d4037',          // brown 700
							color: '#fff',
							fontWeight: 700,
							letterSpacing: 0.8,
							textTransform: 'uppercase',
						}}
					/>
				) : (
					<Chip size="small" label={stage} color={STAGE_COLOR[stage]} />
				)}
				{hasFailureMarker && (
					<Tooltip title={it.last_error || '(no error message)'}>
						<Chip size="small" variant="outlined" color="error" label={`@ ${it.failed_stage}`} />
					</Tooltip>
				)}
				{it.match_counts && (it.match_counts.created || it.match_counts.matched) ? (
					<Tooltip title={
						`Resource match — ${it.match_counts.created} brand-new resource(s) ` +
						`detected on this task, ${it.match_counts.matched} reused from earlier captures.`
					}>
						<Chip size="small" variant="outlined" color="primary"
							label={`+${it.match_counts.created} new · ${it.match_counts.matched} reused`}
							sx={{ fontVariantNumeric: 'tabular-nums' }}
						/>
					</Tooltip>
				) : null}
				{it.total_items_count > 0 && (
					<Tooltip title={
						`${(it.total_items_count - (it.needs_qa_count || 0))} of ${it.total_items_count} ` +
						`item(s) validated OK (AI confident or human-edited)`
					}>
						<Chip size="small" color="success" variant="outlined"
							label={`OK: ${it.total_items_count - (it.needs_qa_count || 0)}/${it.total_items_count}`}
							sx={{ fontWeight: 700 }}
						/>
					</Tooltip>
				)}
				{it.needs_qa_count > 0 && (
					<Tooltip title={`Open the Review Queue scoped to this task (${it.needs_qa_count} item(s) flagged by AI)`}>
						<Chip
							size="small" color="error" variant="filled"
							clickable
							component={RouterLink}
							to={`${VISIBILITY_TRACKER_ROUTES.REVIEW_QUEUE_PATH}?id_task=${it.id_task}`}
							label={`QA: ${it.needs_qa_count}`}
							sx={{ fontWeight: 700, cursor: 'pointer' }}
						/>
					</Tooltip>
				)}
				<ExportChip it={it} />
			</Stack>
		</TableCell>
		<TableCell align="right">
			<Typography variant="body2" color={it.attempts > 0 ? 'error' : 'text.secondary'}>
				{it.attempts || 0}
			</Typography>
		</TableCell>
		<TableCell>
			<Timeline it={it} />
		</TableCell>
		<TableCell>
			<Typography variant="caption" sx={{ color: 'text.secondary' }}>
				{it.qa?.email || '—'}
			</Typography>
		</TableCell>
		<TableCell align="right">
			<Stack direction="row" spacing={0.5} justifyContent="flex-end">
				<Tooltip title="Re-score this task (re-runs visibility computation)">
					<span>
						<IconButton size="small" disabled={busy || !it.session_path}
							onClick={() => rescore(it.id_task)}>
							<CalculateIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<AiReviewButton it={it} busy={busy} aiReviewTask={aiReviewTask} stage={stage} />
				<RematchButton it={it} busy={busy} rematchTask={rematchTask} stage={stage} />
				<Tooltip title="Retry — reset attempts and bounce back to the previous stage">
					<span>
						<IconButton size="small" disabled={busy || !hasFailureMarker}
							onClick={() => retry(it.id_task)}>
							<ReplayIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<Tooltip title={
					stage !== 'reviewed'
						? 'Re-export only available once the task is reviewed'
						: 'Re-export to downstream clients (Puig / Retail)'
				}>
					<span>
						<IconButton size="small"
							disabled={busy || stage !== 'reviewed'}
							onClick={() => reExportTask(it.id_task)}>
							<CloudUploadIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<Tooltip title="Section history — every date this section ran">
					<IconButton size="small" onClick={() => onHistory && onHistory()}>
						<HistoryIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Open in Banners Review">
					<IconButton size="small" component={RouterLink} to={reviewHref}>
						<OpenInNewIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<StageMenu it={it} stage={stage} busy={busy} setStage={setStage} />
				{busy && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
			</Stack>
		</TableCell>
	</TableRow>
}


// Stage-aware action buttons. `level` decides icon opacity + tooltip
// wording: primary at the natural-next stage, faded "Re-run" elsewhere,
// hidden outside the valid window. Always also disabled by busy /
// no-session_path because that's a hard precondition (no GCS payload
// means there's nothing for the worker to read).
function AiReviewButton({ it, busy, aiReviewTask, stage }) {
	const level = actionLevelFor('aiReview', stage)
	const hardDisabled = busy || !it.session_path || level === 'disabled'
	const isRerun = level === 'secondary'
	return <Tooltip title={
		level === 'disabled'
			? `AI review not available at stage '${stage}'`
			: (isRerun
				? "Re-run AI review (tagging will overwrite current values)"
				: "AI review — tag type / categories / brands via vision model"
			)
	}>
		<span>
			<IconButton size="small" disabled={hardDisabled}
				sx={{ opacity: isRerun ? 0.4 : 1 }}
				onClick={() => aiReviewTask(it.id_task)}>
				<AutoAwesomeIcon fontSize="small" />
			</IconButton>
		</span>
	</Tooltip>
}

function RematchButton({ it, busy, rematchTask, stage }) {
	const level = actionLevelFor('rematch', stage)
	const hardDisabled = busy || !it.session_path || level === 'disabled'
	const isRerun = level === 'secondary'
	return <Tooltip title={
		level === 'disabled'
			? `Resource match not available at stage '${stage}'`
			: (isRerun
				? "Re-run resource match (already past this stage)"
				: "Resource match — pHash dedup against the resources master"
			)
	}>
		<span>
			<IconButton size="small" disabled={hardDisabled}
				sx={{ opacity: isRerun ? 0.4 : 1 }}
				onClick={() => rematchTask(it.id_task)}>
				<HubIcon fontSize="small" />
			</IconButton>
		</span>
	</Tooltip>
}


// Kebab menu — manual override of pipeline_stage. Useful for "I already
// reviewed this elsewhere, mark it reviewed" or "this task is broken,
// drop it back to pending so the QA re-uploads".
function StageMenu({ it, stage, busy, setStage }) {
	const [anchor, setAnchor] = useState(null)
	const open = Boolean(anchor)
	const close = () => setAnchor(null)
	return <>
		<Tooltip title="Set stage manually">
			<span>
				<IconButton size="small" disabled={busy}
					onClick={(e) => setAnchor(e.currentTarget)}>
					<MoreVertIcon fontSize="small" />
				</IconButton>
			</span>
		</Tooltip>
		<Menu anchorEl={anchor} open={open} onClose={close}
			PaperProps={{ sx: { minWidth: 200 } }}
		>
			<MenuItem disabled sx={{ opacity: 0.7 }}>
				<Typography variant="caption">Set stage to…</Typography>
			</MenuItem>
			{STAGES.map(s => (
				<MenuItem key={s}
					selected={s === stage}
					onClick={() => { close(); setStage(it.id_task, s) }}
				>
					<ListItemIcon sx={{ minWidth: 30 }}>
						{s === stage ? <CheckIcon fontSize="small" /> : null}
					</ListItemIcon>
					<Chip size="small" label={s} color={STAGE_COLOR[s]} variant="outlined" />
				</MenuItem>
			))}
		</Menu>
	</>
}


// Pipeline timeline: one dot per stage with its label underneath and a
// connecting bar between dots. Each dot has one of four states:
//
//   - 'done'    → solid blue circle, timestamp shown on hover.
//   - 'skipped' → outlined blue circle with a fast-forward arrow inside.
//                 Happens when a later stage has a timestamp but this one
//                 doesn't — most common case is AI review being skipped
//                 because the matcher resolved 100% of leaves against
//                 already-validated resources, so the task jumped from
//                 'matched' straight to 'reviewed'.
//   - 'failed'  → red filled circle with an X. Mapped from the task's
//                 `failed_stage` field; subsequent dots stay 'pending'.
//   - 'pending' → empty grey circle.
//
// Connecting bars between dots reflect both endpoints: solid blue when
// both are done (or one done + one skipped, which is still forward
// progress), dashed grey otherwise, red if either endpoint is failed.
const TIMELINE_STAGES = [
	{ key: 'started_at',     label: 'in progress', short: 'started' },
	{ key: 'extracted_at',   label: 'extracted',   short: 'extracted' },
	{ key: 'scored_at',      label: 'scored',      short: 'scored' },
	{ key: 'matched_at',     label: 'matched',     short: 'matched' },
	{ key: 'ai_reviewed_at', label: 'AI reviewed', short: 'AI rev.' },
	{ key: 'reviewed_at',    label: 'reviewed',    short: 'reviewed' },
]

// Map failed_stage values to the step index they belong to. Used to
// paint the red dot on the right step when a task lands at failed.
const FAILED_STAGE_TO_STEP = {
	extraction:          1, // failed during extraction → 'extracted' step
	scoring:             2, // failed during scoring    → 'scored'    step
	'match':             3,
	'match-enqueue':     3,
	'ai-review':         4,
	'ai-review-enqueue': 4,
}

function deriveTimelineStates(it) {
	const tsList = TIMELINE_STAGES.map(s => it[s.key] || null)
	// A task is in a failed state whenever it carries a failed_stage —
	// pipeline_stage might still be 'matched' for cases like
	// ai-review-enqueue where the chain failed AFTER the matcher
	// successfully bumped the stage. The dot needs to be red regardless.
	const failedStep = it.failed_stage
		? (FAILED_STAGE_TO_STEP[it.failed_stage] ?? -1)
		: -1
	const lastDoneIdx = tsList.reduce((acc, ts, i) => ts ? i : acc, -1)
	return tsList.map((ts, i) => {
		if (i === failedStep) return 'failed'
		if (ts) return 'done'
		if (i < lastDoneIdx) return 'skipped'
		return 'pending'
	})
}

function Timeline({ it }) {
	const states = deriveTimelineStates(it)
	return <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 360 }}>
		{TIMELINE_STAGES.map((s, i) => {
			const state = states[i]
			const ts = it[s.key]
			const tooltipText = state === 'failed'
				? `${s.label}: failed — ${it.last_error || '(no error)'}`
				: state === 'skipped'
					? `${s.label}: skipped (jumped past this step)`
					: state === 'done'
						? `${s.label}: ${formatTs(ts)}`
						: `${s.label}: not yet`
			return <React.Fragment key={s.key}>
				<TimelineStep state={state} label={s.short} tooltip={tooltipText} />
				{i < TIMELINE_STAGES.length - 1 && (
					<TimelineBar from={state} to={states[i + 1]} />
				)}
			</React.Fragment>
		})}
	</Box>
}

function TimelineStep({ state, label, tooltip }) {
	const palette = {
		done:    { bg: 'primary.main',   fg: 'common.white', border: 'primary.main' },
		skipped: { bg: 'transparent',    fg: 'primary.main', border: 'primary.main' },
		pending: { bg: 'transparent',    fg: 'grey.500',     border: 'grey.300'     },
		failed:  { bg: 'error.main',     fg: 'common.white', border: 'error.main'   },
	}[state]
	const labelColor = state === 'pending' ? 'text.disabled' : 'text.primary'
	const labelWeight = state === 'failed' || state === 'done' ? 600 : 400
	return <Tooltip title={tooltip}>
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 56 }}>
			<Box sx={{
				width: 18, height: 18, borderRadius: '50%',
				border: '2px solid', borderColor: palette.border,
				bgcolor: palette.bg, color: palette.fg,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				fontSize: 11, lineHeight: 1, fontWeight: 700,
			}}>
				{state === 'done'    && '✓'}
				{state === 'skipped' && '»'}
				{state === 'failed'  && '✕'}
			</Box>
			<Typography variant="caption" sx={{
				mt: 0.4, fontSize: 10, lineHeight: 1.2,
				color: labelColor, fontWeight: labelWeight,
				whiteSpace: 'nowrap',
			}}>
				{label}
			</Typography>
		</Box>
	</Tooltip>
}

function TimelineBar({ from, to }) {
	// Color rules — endpoints first, then a fallback. Red wins; otherwise
	// any pair that includes a "real progress" endpoint (done/skipped)
	// gets the blue bar; only fully-pending → pending stays grey.
	const isFailed = from === 'failed' || to === 'failed'
	const isDone   = (from === 'done' || from === 'skipped') &&
	                 (to   === 'done' || to   === 'skipped')
	const color = isFailed ? 'error.main' : (isDone ? 'primary.main' : 'grey.300')
	const dashed = !isDone && !isFailed
	return <Box sx={{
		flex: 1, height: 2, mt: '8px',
		// MUI sx supports background colour aliases via theme. For the
		// dashed grey case we draw a 1D dashed line; for solid we just
		// flatten the box's background.
		...(dashed
			? { backgroundImage: `repeating-linear-gradient(to right, currentColor 0 4px, transparent 4px 7px)`, color, bgcolor: 'transparent' }
			: { bgcolor: color }),
	}} />
}


function formatTs(iso) {
	if (!iso) return ''
	try {
		const d = new Date(iso)
		const time = d.toTimeString().slice(0, 8)
		const date = d.toISOString().slice(0, 10)
		return `${date} ${time}`
	} catch {
		return iso
	}
}


// Section history drawer — every date this section ran, newest first.
// Opened from a row's history icon; fetches /admin/sections/<id>/history.
